import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts"

import { api } from "@/lib/api"
import { formatDayLabel, formatDayTick } from "./week-label"

const SERIES = [
	{ key: "mau", label: "MAU", color: "#4CB782" },
	{ key: "wau", label: "WAU", color: "#8B5CF6" },
	{ key: "dau", label: "DAU", color: "#2563EB" },
	{ key: "activeWorkspaces", label: "Active workspaces", color: "#F59E0B" },
] as const

interface TooltipProps {
	active?: boolean
	payload?: Array<{ value: number; name?: string; dataKey?: string }>
	label?: string
}

export function ActiveUsersChart() {
	const query = api.analytics.activeUsersChart.useQuery({})
	const chartData = query.data?.chartData

	const hasWorkspaces =
		chartData?.some((d) => d.activeWorkspaces !== null) ?? false

	const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
		if (!active || !payload?.length) return null
		const point = chartData?.find((d) => d.date === label)
		return (
			<div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
				<p className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
					{label ? formatDayLabel(label) : ""}
				</p>
				<div className="space-y-1">
					{SERIES.map((s) => {
						const item = payload.find((p) => p.dataKey === s.key)
						if (s.key === "activeWorkspaces" && !hasWorkspaces) return null
						return (
							<p
								key={s.key}
								className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
							>
								<span
									className="h-2 w-2 rounded-full"
									style={{ backgroundColor: s.color }}
								/>
								{s.label}: {item?.value ?? 0}
							</p>
						)
					})}
					{point ? (
						<p className="border-t border-gray-200 pt-1 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-400">
							Stickiness (DAU/MAU): {point.stickiness}%
						</p>
					) : null}
				</div>
			</div>
		)
	}

	return (
		<div className="rounded-lg border p-4">
			<div className="mb-3 flex items-center justify-between">
				<h3 className="text-sm font-medium text-gray-800 dark:text-white">
					Active users (PostHog)
				</h3>
				<div className="flex flex-wrap gap-4">
					{SERIES.map((s) => {
						if (s.key === "activeWorkspaces" && !hasWorkspaces) return null
						return (
							<div key={s.key} className="flex items-center gap-2">
								<div
									className="h-3 w-3 rounded"
									style={{ backgroundColor: s.color }}
								/>
								<span className="text-xs text-gray-600 dark:text-gray-400">
									{s.label}
								</span>
							</div>
						)
					})}
				</div>
			</div>

			<div className="h-64">
				{chartData && !query.isLoading ? (
					<div className="h-full w-full [&_*]:outline-none">
						<ResponsiveContainer width="100%" height="100%">
							<LineChart data={chartData} margin={{ left: -20, right: 8 }}>
								<CartesianGrid
									className="stroke-gray-200 dark:stroke-gray-700"
									vertical={false}
								/>
								<XAxis
									dataKey="date"
									tickFormatter={formatDayTick}
									minTickGap={48}
									className="text-xs text-gray-500 dark:text-gray-400"
									tickLine={false}
									axisLine={{
										className: "stroke-gray-300 dark:stroke-gray-700",
									}}
								/>
								<YAxis
									className="text-xs text-gray-500 dark:text-gray-400"
									axisLine={false}
									tickLine={false}
									tickFormatter={(value: number) =>
										Math.round(value).toString()
									}
								/>
								<Tooltip
									content={<CustomTooltip />}
									cursor={{ stroke: "rgba(0, 0, 0, 0.1)" }}
								/>
								{SERIES.map((s) => {
									if (s.key === "activeWorkspaces" && !hasWorkspaces)
										return null
									return (
										<Line
											key={s.key}
											type="monotone"
											dataKey={s.key}
											stroke={s.color}
											strokeWidth={2}
											dot={false}
											activeDot={{ r: 4 }}
										/>
									)
								})}
							</LineChart>
						</ResponsiveContainer>
					</div>
				) : query.isError ? (
					<div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500 dark:text-gray-400">
						Could not load PostHog data. Confirm POSTHOG_PERSONAL_ALL_ACCESS is
						set to a personal API key with query scope.
					</div>
				) : (
					<div className="flex h-full animate-pulse items-end justify-between gap-2 px-6">
						{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
							<div
								key={i}
								className="w-full rounded-t bg-gray-200 dark:bg-gray-700"
								style={{ height: `${i * 6 + 20}%` }}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	)
}
